/**
 * Localization-ready string table. All user-facing UI text should flow
 * through t(); adding a locale = adding a catalog object here (or, later,
 * loading one). Missing keys fall back to English, then to the key itself —
 * loudly in dev via console.warn, never silently breaking layout.
 */
export type LocaleCatalog = Record<string, string>;

const en: LocaleCatalog = {
  tagline: 'Run · Jump · Shift',
  'menu.editor': 'Level Editor',
  'menu.garage': 'Garage',
  'menu.howto': 'How to Play',
  'menu.settings': 'Settings',
  'gate.title': 'Sign in to play',
  'gate.subtitle': 'Your progress, stars, and levels follow your account on any device.',
  'gate.create-title': 'Create account',
  'gate.username': 'User name',
  'gate.offline': 'Servers unreachable.',
  'gate.play-offline': 'Play offline',
  'gate.lost': 'Lost password',
  'gate.next': 'Next',
  'gate.back': 'Back',
  'gate.security-q': 'Security question',
  'gate.security-a': 'Your answer',
  'gate.answer-ph': 'Type your answer…',
  'gate.new-password': 'New password',
  'gate.save-password': 'Save password',
  'gate.reset-done': 'Password updated — sign in with it below.',
  'gate.err.no-user': 'User name does not exist.',
  'gate.err.answer-required': 'Choose a security question and type your answer.',
  'gate.use-start-screen': 'Sign in from the start screen.',
  'gate.err.wrong-answer': 'Incorrect, try again.',
  'gate.err.no-question': 'This account has no security question set.',
  'gate.q1': 'What was the name of your first pet?',
  'gate.q2': 'What street did you grow up on?',
  'gate.q3': 'What was the name of your first school?',
  'gate.q4': 'What city were you born in?',
  'gate.q5': "What is your favorite childhood friend's name?",
  'gate.q6': 'What was your first video game?',
  'menu.prev': 'Previous level',
  'menu.next': 'Next level',
  'menu.card.fresh': '{track} · difficulty {diff}',
  'menu.card.played': 'best {best}% · {attempts} attempts · {clears} clears',
  'hud.practice': 'PRACTICE',
  'hud.testing': 'TESTING — ESC returns to editor',
  'hud.attempt': 'Attempt {n}',
  'hud.death': 'death',
  'hud.deaths': 'deaths',
  'hud.move-left': 'Move left',
  'hud.move-right': 'Move right',
  'hud.pause': 'Pause',
  'pause.title': 'Paused',
  'pause.resume': 'Resume',
  'pause.restart': 'Restart',
  'pause.exit': 'Exit to Menu',
  'pause.practice-on': 'Practice: On',
  'pause.practice-off': 'Practice: Off',
  'pause.hint': 'Practice mode: <kbd>Z</kbd> place checkpoint · <kbd>X</kbd> remove',
  'complete.title': 'Level Complete!',
  'complete.again': 'Play Again',
  'complete.level': 'Level',
  'complete.attempts': 'Attempts',
  'complete.jumps': 'Jumps this run',
  'complete.time': 'Time',
  'complete.total': 'Total completions',
  'complete.rewards': 'Rewards',
  'garage.title': 'Garage',
  'garage.primary': 'Primary color',
  'garage.secondary': 'Secondary color',
  'garage.core': 'Core',
  'garage.trail': 'Trail',
  'garage.burst': 'Death burst',
  'garage.shell': 'Shell',
  'garage.shop': 'Shop (spend ⬡ Flux)',
  'garage.cache': 'Free cache',
  'garage.contracts': 'Contracts (rotate daily)',
  'garage.circuits': 'Circuits (level collections)',
  'garage.achievements': 'Achievements',
  'garage.stats': 'Lifetime stats',
  'garage.done': 'Done',
  'garage.open-cache': 'Open cache',
  'garage.next-cache': 'Next cache in {min} min',
  'garage.claimed': 'claimed',
  'garage.claim': 'Claim',
  'garage.owned': 'owned',
  'garage.requires': 'requires',
  'garage.levels': 'levels',
  'garage.stat.jumps': 'Jumps',
  'garage.stat.attempts': 'Attempts',
  'garage.stat.completions': 'Completions',
  'garage.stat.shards': 'Shards',
  'garage.stat.playtime': 'Play time',
  'garage.stat.playtime-min': '{min} min',
  'settings.title': 'Settings',
  'settings.music': 'Music volume',
  'settings.sfx': 'Effects volume',
  'settings.offset': 'Audio offset (ms)',
  'settings.glow': 'Glow effects',
  'settings.particles': 'Particles',
  'settings.flash': 'Reduced flashing',
  'settings.motion': 'Reduced motion',
  'settings.ldm': 'Low detail mode',
  'settings.export': 'Export save',
  'settings.import': 'Import save',
  'settings.done': 'Done',
  'settings.preset': 'Quality preset',
  'settings.preset.auto': 'Auto-detect',
  'settings.preset.low': 'Low',
  'settings.preset.medium': 'Medium',
  'settings.preset.high': 'High',
  'settings.uiscale': 'UI scale',
  'settings.contrast': 'High contrast UI',
  'settings.colorblind': 'Colorblind shapes',
  'settings.fullscreen': 'Fullscreen',
  'settings.calibrate': 'Calibrate audio offset',
  'settings.key-jump': 'Jump key',
  'settings.key-left': 'Move left key',
  'settings.key-right': 'Move right key',
  'settings.key-listen': 'Press a key…',
  'settings.pad-jump': 'Jump button (pad)',
  'settings.pad-listen': 'Press a pad button…',
  'settings.fpscap': 'FPS cap',
  'settings.fpscap.off': 'Uncapped',
  'settings.renderscale': 'Render scale',
  'settings.key-current': '{keys}',
  'calibrate.title': 'Audio calibration',
  'calibrate.body':
    'Tap (or press any key) on each click. After 12 clicks your offset is measured and applied.',
  'calibrate.progress': '{n} / {total} taps',
  'calibrate.result': 'Offset set to {ms} ms',
  'calibrate.cancel': 'Cancel',
  'audio.missing': 'Song "{song}" unavailable — playing silent.',
  'howto.title': 'How to Play',
  'howto.body':
    'Your shape runs on its own — you control the jump.<br><br>' +
    '<kbd>Space</kbd> / <kbd>↑</kbd> / <kbd>W</kbd> / click / tap — jump<br>' +
    'Hold to keep jumping. <kbd>Esc</kbd> — pause. <kbd>R</kbd> — restart.<br><br>' +
    'Avoid the glowing spikes. Reach the green gate.<br>' +
    'Ride the rhythm — obstacles follow the beat.',
  'howto.close': 'Got it',
  // --- Online ---
  'menu.online': 'Online',
  'online.title': 'Online',
  'online.offline': 'Offline — servers unreachable. Everything local still works.',
  'online.login': 'Log in',
  'online.register': 'Create account',
  'online.logout': 'Log out',
  'online.email': 'Email',
  'online.handle': 'Handle',
  'online.password': 'Password',
  'online.signed-in': 'Signed in as {handle}',
  'online.cloud': 'Cloud save',
  'online.sync': 'Sync now',
  'online.sync-ok': 'Synced (revision {rev})',
  'online.sync-conflict': 'Conflict: this device and the cloud both changed. Choose which to keep.',
  'online.keep-local': 'Keep this device',
  'online.keep-cloud': 'Keep cloud copy',
  'online.showcase': 'Showcase',
  'online.daily': 'Daily Shift',
  'online.weekly': 'Weekly Gauntlet',
  'online.featured': 'Featured',
  'online.browse': 'Community levels',
  'online.search': 'Search levels or authors…',
  'online.sort.recent': 'Newest',
  'online.sort.downloads': 'Most played',
  'online.sort.likes': 'Most liked',
  'online.sort.difficulty': 'By difficulty',
  'online.play': 'Play',
  'online.board': 'Board',
  'online.comments': 'Comments',
  'online.comment-ph': 'Say something nice…',
  'online.send': 'Send',
  'online.vote': 'Rate difficulty',
  'online.report': 'Report',
  'online.report-ph': 'What is wrong?',
  'online.friends': 'Friends',
  'online.add-friend': 'Add by handle…',
  'online.accept': 'Accept',
  'online.pending': 'pending',
  'online.no-results': 'Nothing here yet.',
  'online.favorite': 'Favorite',
  'online.like': 'Like',
  'online.favorites': '★ Favorites',
  'online.all-levels': 'All levels',
  'online.profile.joined': 'Joined {date}',
  'online.profile.published': '{n} published',
  'online.profile.clears': '{n} full clears',
  'online.profile.back': 'Back',
  'online.block': 'Block',
  'online.unblock': 'Unblock',
  'online.message': 'Message',
  'online.message-ph': 'Write a message…',
  'online.inbox': 'Inbox',
  'online.mod': 'Moderation',
  'online.mod.queue': 'Report queue',
  'online.mod.resolve': 'Resolve',
  'online.mod.remove-level': 'Remove level',
  'online.mod.remove-comment': 'Remove comment',
  'online.mod.feature': 'Feature',
  'online.mod.schedule': 'Schedule daily/weekly',
  'online.mod.schedule-ph': 'YYYY-MM-DD for Daily Shift, YYYY-Www for Weekly Gauntlet',
  'online.mod.reason-ph': 'Resolution / reason…',
  'online.publish': 'Publish online',
  'online.published': 'Published! Level #{id} is live.',
  'online.score-sent': 'Run submitted: {percent}% verified server-side.',
  'online.close': 'Close',
  // --- Admin console ---
  'menu.admin': 'Admin',
  'admin.title': 'Admin console',
  'admin.reports': 'Report queue',
  'admin.curation': 'Curation',
  'admin.curation.search': 'Search levels to curate…',
  'admin.curation.featured': 'Featured now',
  'admin.curation.daily': 'Daily Shift',
  'admin.curation.weekly': 'Weekly Gauntlet',
  'admin.unfeature': 'Unfeature',
  'admin.remove': 'Remove',
  'admin.users': 'Users',
  'admin.users.search': 'Search users by handle…',
  'admin.ban': 'Ban',
  'admin.unban': 'Unban',
  'admin.ban-reason': 'Ban reason…',
  'admin.delete': 'Delete',
  'admin.delete-confirm':
    'Permanently delete "{handle}"? Their account, published levels, scores, comments, and messages will all be erased. This cannot be undone.',
  'admin.banned': 'banned',
  'admin.joined': 'joined {date}',
  'admin.audit': 'Audit log',
  'admin.refresh': 'Refresh',
  'admin.self': '(you)',
  'admin.no-results': 'Nothing here.',
  // --- The Vault ---
  'vault.title': 'The Vault',
  'vault.hint': 'Shards feed the Vault. The Vault remembers.',
  'vault.progress': '{n} shards collected',
  'vault.claim': 'Claim',
  'vault.claimed': 'claimed',
  'vault.locked': '{n} shards required',
  'vault.close': 'Seal the Vault',
  // --- Editor chrome ---
  'ed.file': 'File ▾',
  'ed.file.new': 'New',
  'ed.file.open': 'Open…',
  'ed.file.save': 'Save',
  'ed.file.saveas': 'Save As…',
  'ed.file.rename': 'Rename…',
  'ed.file.duplicate': 'Duplicate',
  'ed.file.export': 'Export .ssl',
  'ed.file.import': 'Import .ssl',
  'ed.file.snapshot': 'Take Snapshot',
  'ed.file.restore': 'Restore Snapshot…',
  'ed.file.delete': 'Delete Level',
  'ed.undo.title': 'Undo (Ctrl+Z)',
  'ed.redo.title': 'Redo (Ctrl+Y)',
  'ed.mode.build': 'Build',
  'ed.mode.build.title': 'Build (1)',
  'ed.mode.edit': 'Edit',
  'ed.mode.edit.title': 'Edit (2)',
  'ed.mode.delete': 'Del',
  'ed.mode.delete.title': 'Delete (3)',
  'ed.grid': 'Grid',
  'ed.grid.title': 'Grid (G)',
  'ed.snap': 'Snap',
  'ed.snap.title': 'Snap (H)',
  'ed.hitbox': 'Hitbox',
  'ed.hitbox.title': 'Hitboxes (B)',
  'ed.flow': 'Flow',
  'ed.flow.title': 'Trigger flow (F)',
  'ed.beats': 'Beats',
  'ed.beats.title': 'Beat guides from the song BPM',
  'ed.filter.title': 'Selection filter (Edit mode)',
  'ed.filter.all': 'All types',
  'ed.settings': 'Settings',
  'ed.play': '▶ Test',
  'ed.play.title': 'Playtest (Space)',
  'ed.exit': 'Exit',
  'ed.tutorial': 'Tutorial',
  'ed.search': 'Search objects…',
  'ed.objects': '{n} objects',
  'ed.recent': 'Recent',
  'ed.untitled': 'Untitled Level',
  // --- Editor inspector ---
  'ed.inspector': 'Inspector',
  'ed.inspector.selected': 'Inspector — {n} selected',
  'ed.inspector.empty':
    'Nothing selected.<br>Build: click to place.<br>Edit: click/drag to select, ' +
    'drag selection to move.<br>Middle/right-drag pans, wheel zooms.',
  'ed.delete-sel': 'Delete (Del)',
  'ed.trigger-title': '{kind} trigger',
  // --- Editor dialogs & prompts ---
  'ed.level-settings': 'Level Settings',
  'ed.apply': 'Apply',
  'ed.cancel': 'Cancel',
  'ed.confirm-exit': 'Save changes before exiting? (Cancel keeps you in the editor)',
  'ed.confirm-new': 'Discard unsaved changes and create a new level?',
  'ed.confirm-delete': 'Delete "{name}" permanently?',
  'ed.confirm-draft': 'Recover unsaved draft from {when}?',
  'ed.prompt-saveas': 'Save as (level name):',
  'ed.prompt-rename': 'Rename level:',
  'ed.prompt-restore': 'Restore which snapshot?\n{list}\n\nEnter a number:',
  'ed.prompt-open': 'Open which level?\n{list}\n\nEnter a number:',
  'ed.snapshot-saved': 'Snapshot saved.',
  'ed.no-snapshots': 'No snapshots for this level yet.',
  'ed.no-levels': 'No saved levels yet.',
  'ed.level-problems': 'Level has problems:\n\n{errors}',
  'ed.copy-suffix': '{name} copy',
  // --- Editor tutorial ---
  'ed.tut.progress': 'Tutorial {n}/{total}',
  'ed.tut.skip': 'Skip tutorial',
  'ed.tut.1.title': 'Place a block',
  'ed.tut.1.body':
    'You are in Build mode. Pick <b>Block</b> from the palette on the left, then click the canvas to place it.',
  'ed.tut.2.title': 'Add a hazard',
  'ed.tut.2.body': 'Now pick <b>Spike</b> from the palette and place one a few blocks ahead.',
  'ed.tut.3.title': 'Select something',
  'ed.tut.3.body':
    'Switch to <b>Edit</b> mode (press 2 or click Edit) and click one of your objects to select it. The inspector on the right shows its properties.',
  'ed.tut.4.title': 'Drop in a trigger',
  'ed.tut.4.body':
    'Back in <b>Build</b> mode, open the <b>Triggers</b> category and place any trigger — try <b>Move</b>. Triggers animate the level while you play.',
  'ed.tut.5.title': 'Playtest it',
  'ed.tut.5.body': 'Press <b>▶ Test</b> (or Space) to play your level. Esc returns to the editor.',
  'ed.tut.done.title': 'Tutorial complete!',
  'ed.tut.done.body':
    'You know the essentials: place, edit, trigger, playtest. Everything else lives in the File menu and the inspector. Have fun building!',
  // --- Editor field labels ---
  'ed.f.x': 'x (blocks)',
  'ed.f.y': 'y (blocks)',
  'ed.f.rot': 'rotation °',
  'ed.f.z': 'z-order',
  'ed.f.flipy': 'flip Y',
  'ed.f.groups': 'groups',
  'ed.f.groups-ph': 'e.g. 1,2',
  'ed.f.channel': 'color ch.',
  'ed.f.hue': 'hue ±',
  'ed.f.sat': 'sat ±',
  'ed.f.val': 'val ±',
  'ed.f.ldm': 'low-detail',
  'ed.f.dx': 'dx (blocks)',
  'ed.f.dy': 'dy (blocks)',
  'ed.f.direction': 'direction',
  'ed.f.grade': 'grade',
  'ed.f.target': 'target group',
  'ed.f.itemid': 'item id',
  'ed.f.amount': 'amount',
  'ed.f.threshold': 'threshold',
  'ed.f.spawn-group': 'spawn group',
  'ed.f.trg-channel': 'channel',
  'ed.f.color': 'color',
  'ed.f.color-b': 'color B',
  'ed.f.alpha': 'alpha',
  'ed.f.vertical': 'vertical',
  'ed.f.radius': 'radius (blocks)',
  'ed.f.width': 'width (blocks)',
  'ed.f.degrees': 'degrees',
  'ed.f.scale': 'scale',
  'ed.f.path': 'path',
  'ed.f.loop': 'loop',
  'ed.f.side': 'side',
  'ed.f.strength': 'strength',
  'ed.f.factor': 'factor',
  'ed.f.visible': 'visible',
  'ed.f.delay': 'delay (s)',
  'ed.f.zoom': 'zoom',
  'ed.f.duration': 'duration (s)',
  'ed.f.easing': 'easing',
  'ed.f.spawnonly': 'spawn-only',
  'ed.f.text': 'text',
  'ed.f.size': 'size',
  'ed.f.needed': 'needed',
  'ed.f.form': 'form',
  'ed.f.speed': 'speed',
  'ed.f.mode': 'mode',
  'ed.f.song': 'song',
  'ed.f.start-form': 'start form',
  'ed.f.accent': 'accent',
  'ed.f.hazard': 'hazard',
  'ed.f.background': 'background',
};

const CATALOGS: Record<string, LocaleCatalog> = { en };

let activeLocale = 'en';
const warned = new Set<string>();

export function setLocale(locale: string): void {
  if (!CATALOGS[locale]) {
    console.warn(`Unknown locale "${locale}"; staying on "${activeLocale}".`);
    return;
  }
  activeLocale = locale;
}

/** Translate a key; substitutes {var} placeholders from vars. */
export function t(key: string, vars?: Record<string, string | number>): string {
  const catalog = CATALOGS[activeLocale] ?? en;
  let text = catalog[key] ?? en[key];
  if (text === undefined) {
    if (!warned.has(key)) {
      console.warn(`Missing i18n key: ${key}`);
      warned.add(key);
    }
    return key;
  }
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      text = text.replaceAll(`{${k}}`, String(v));
    }
  }
  return text;
}
